import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '@timely/ui';
import { FormNotice } from '../screens/AuthLayout';
import { useWorkspaces } from './WorkspaceProvider';
import { LifecycleError, cancelDeletion, formatWhen, type PausedWorkspace } from '../lifecycle/lifecycle';

/**
 * A company that is not in ordinary use, as its own members see it.
 *
 * Row Level Security hides such a company from every ordinary read, which is
 * the point — nothing in it works — but its people are told why, and when,
 * and an owner is given the one thing they can still do: cancel.
 */
export function PausedCompanyCard({ workspace }: { workspace: PausedWorkspace }) {
    const { reload } = useWorkspaces();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cancel = async () => {
        setBusy(true);
        setError(null);
        try {
            await cancelDeletion(workspace.workspace_id);
            await reload();
            navigate(`/w/${workspace.slug}/today`, { replace: true });
        } catch (e) {
            setError(e instanceof LifecycleError ? e.message : 'That did not work. Try again.');
            await reload();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="px-7 py-7" role="region" aria-label={workspace.name}>
            {workspace.status === 'deletion_pending' && (
                <>
                    <h1 className="text-[19px] font-bold text-label tracking-[-0.02em]">
                        {workspace.name} is scheduled for deletion
                    </h1>
                    <p className="text-[14px] text-label-2 mt-3 leading-relaxed" data-testid="deletion-deadline">
                        It will be permanently deleted on <strong className="text-label">{formatWhen(workspace.deletion_scheduled_for)}</strong>.
                        Until then nothing has been removed, and the company is closed to everyone.
                    </p>
                    {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}
                    <div className="mt-6">
                        {workspace.is_owner ? (
                            <Button variant="primary" onClick={() => { void cancel(); }} disabled={busy} data-testid="cancel-deletion">
                                {busy ? 'Cancelling…' : 'Cancel deletion and reopen the company'}
                            </Button>
                        ) : (
                            <p className="text-[13.5px] text-label-3">Only an owner of the company can cancel the deletion.</p>
                        )}
                    </div>
                </>
            )}
            {workspace.status === 'deleting' && (
                <>
                    <h1 className="text-[19px] font-bold text-label tracking-[-0.02em]">{workspace.name} is being deleted</h1>
                    <p className="text-[14px] text-label-2 mt-3 leading-relaxed">
                        The grace period has ended and deletion has begun. It can no longer be cancelled.
                    </p>
                </>
            )}
            {workspace.status === 'suspended' && (
                <>
                    <h1 className="text-[19px] font-bold text-label tracking-[-0.02em]">{workspace.name} is suspended</h1>
                    <p className="text-[14px] text-label-2 mt-3 leading-relaxed">
                        This company has been suspended and cannot be used at the moment.
                    </p>
                </>
            )}
        </Card>
    );
}

/** One or more paused companies, when that is all a person has — or asked for. */
export function PausedCompanies({ workspaces, showOthersLink }: { workspaces: PausedWorkspace[]; showOthersLink: boolean }) {
    return (
        <main className="min-h-dvh grid place-items-center px-4 py-10">
            <div className="max-w-lg w-full space-y-4">
                {workspaces.map(w => <PausedCompanyCard key={w.workspace_id} workspace={w} />)}
                {showOthersLink && (
                    <div className="text-center">
                        <Button variant="ghost" onClick={() => { window.location.href = '/'; }}>Go to your other companies</Button>
                    </div>
                )}
            </div>
        </main>
    );
}
